import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import Loading from '../common/Loading';
import Button from '../common/Button';
import CreateSprint from './CreateSprint';
import SprintBoard from './SprintBoard';

const Sprints = () => {
  const { userProfile } = useAuth();
  const [sprints, setSprints] = useState([]);
  const [activeSprint, setActiveSprint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [view, setView] = useState('board');

  useEffect(() => {
    const fetchSprints = async () => {
      if (!userProfile) return;

      try {
        // Get user's projects first
        const projectsQuery = query(
          collection(db, 'projects'),
          where('members', 'array-contains', userProfile.uid)
        );
        const projectsSnapshot = await getDocs(projectsQuery);
        const projectIds = projectsSnapshot.docs.map(doc => doc.id);

        if (projectIds.length === 0) {
          setSprints([]);
          return;
        }

        // Get sprints from user's projects
        const sprintsQuery = query(
          collection(db, 'sprints'),
          where('projectId', 'in', projectIds),
          orderBy('startDate', 'desc')
        );
        const snapshot = await getDocs(sprintsQuery);
        const sprintsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setSprints(sprintsData);
        
        // Find active sprint
        const now = new Date();
        const active = sprintsData.find(sprint => 
          new Date(sprint.startDate) <= now && new Date(sprint.endDate) >= now
        );
        setActiveSprint(active);
      } catch (error) {
        console.error('Error fetching sprints:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSprints();
  }, [userProfile]);

  const handleSprintCreated = (newSprint) => {
    setSprints(prev => [newSprint, ...prev]);
    setShowCreateModal(false);
  };

  const handleSprintDeleted = (sprintId) => {
    setSprints(prev => prev.filter(sprint => sprint.id !== sprintId));
    if (activeSprint?.id === sprintId) {
      setActiveSprint(null);
    }
  };

  const startSprint = async (sprintId) => {
    try {
      await updateDoc(doc(db, 'sprints', sprintId), {
        status: 'active',
        startedAt: new Date().toISOString()
      });
      
      setSprints(prev => prev.map(sprint => 
        sprint.id === sprintId 
          ? { ...sprint, status: 'active', startedAt: new Date().toISOString() }
          : sprint
      ));
      
      const updatedSprint = sprints.find(s => s.id === sprintId);
      setActiveSprint({ ...updatedSprint, status: 'active', startedAt: new Date().toISOString() });
    } catch (error) {
      console.error('Error starting sprint:', error);
      alert('Failed to start sprint');
    }
  };

  const completeSprint = async (sprintId) => {
    try {
      await updateDoc(doc(db, 'sprints', sprintId), {
        status: 'completed',
        completedAt: new Date().toISOString()
      });
      
      setSprints(prev => prev.map(sprint => 
        sprint.id === sprintId 
          ? { ...sprint, status: 'completed', completedAt: new Date().toISOString() }
          : sprint
      ));
      
      if (activeSprint?.id === sprintId) {
        setActiveSprint(null);
      }
    } catch (error) {
      console.error('Error completing sprint:', error);
      alert('Failed to complete sprint');
    }
  };

  if (loading) {
    return <Loading text="Loading sprints..." />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Sprints
          </h1>
          {activeSprint && (
            <p className="text-green-600 dark:text-green-400 mt-1">
              Active Sprint: {activeSprint.name} (Ends {new Date(activeSprint.endDate).toLocaleDateString()})
            </p>
          )}
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          variant="primary"
        >
          Create Sprint
        </Button>
      </div>

      {activeSprint && view === 'board' && (
        <div className="mb-6">
          <SprintBoard sprint={activeSprint} />
        </div>
      )}

      {/* Sprint List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              All Sprints
            </h2>
            <div className="flex space-x-2">
              <button
                onClick={() => setView('list')}
                className={`px-3 py-1 rounded-lg text-sm ${
                  view === 'list' 
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' 
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                List
              </button>
              <button
                onClick={() => setView('board')}
                className={`px-3 py-1 rounded-lg text-sm ${
                  view === 'board' 
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' 
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                Board
              </button>
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {sprints.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 dark:text-gray-500 text-6xl mb-4">📅</div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No sprints yet
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                Create your first sprint to start planning
              </p>
              <Button
                onClick={() => setShowCreateModal(true)}
                variant="primary"
              >
                Create Sprint
              </Button>
            </div>
          ) : (
            sprints.map(sprint => (
              <SprintItem
                key={sprint.id}
                sprint={sprint}
                onStart={startSprint}
                onComplete={completeSprint}
                onDelete={handleSprintDeleted}
                isActive={activeSprint?.id === sprint.id}
              />
            ))
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateSprint
          onClose={() => setShowCreateModal(false)}
          onSprintCreated={handleSprintCreated}
        />
      )}
    </div>
  );
};

const SprintItem = ({ sprint, onStart, onComplete, onDelete, isActive }) => {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${sprint.name}"?`)) {
      return;
    }

    try {
      setDeleting(true);
      await deleteDoc(doc(db, 'sprints', sprint.id));
      onDelete(sprint.id);
    } catch (error) {
      console.error('Error deleting sprint:', error);
      alert('Failed to delete sprint');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planned': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'completed': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  const calculateProgress = () => {
    if (!sprint.totalIssues) return 0;
    return Math.round((sprint.completedIssues / sprint.totalIssues) * 100);
  };

  return (
    <div className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {sprint.name}
            </h3>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(sprint.status)}`}>
              {sprint.status?.charAt(0).toUpperCase() + sprint.status?.slice(1)}
            </span>
            {isActive && (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                Active
              </span>
            )}
          </div>
          
          <p className="text-gray-600 dark:text-gray-400 mb-3">
            {sprint.goal}
          </p>
          
          <div className="flex items-center space-x-6 text-sm text-gray-500 dark:text-gray-400">
            <span>
              {new Date(sprint.startDate).toLocaleDateString()} - {new Date(sprint.endDate).toLocaleDateString()}
            </span>
            <span>
              {sprint.totalIssues || 0} issues
            </span>
            <span>
              {sprint.completedIssues || 0} completed
            </span>
          </div>
          
          {sprint.totalIssues > 0 && (
            <div className="mt-3">
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                <span>Progress</span>
                <span>{calculateProgress()}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${calculateProgress()}%` }}
                />
              </div>
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-2 ml-6">
          {sprint.status === 'planned' && (
            <Button
              size="small"
              onClick={() => onStart(sprint.id)}
            >
              Start Sprint
            </Button>
          )}
          
          {sprint.status === 'active' && (
            <Button
              variant="success"
              size="small"
              onClick={() => onComplete(sprint.id)}
            >
              Complete Sprint
            </Button>
          )}
          
          <Button
            variant="danger"
            size="small"
            loading={deleting}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Sprints;